import type {
  FrameProjection,
  PaintableFrameKind,
  PaintDensity,
  PaintMode,
  PaintSurface,
} from './types';

/**
 * Desired densities when a surface is allowed to paint this kind.
 * Exclusive mode zeroes the inactive surface; dual keeps both.
 */
const KIND_BASE: Record<PaintableFrameKind, FrameProjection> = {
  evidence: {
    dock: 'compact',
    window: 'full',
  },
};

/** Default product mode until dual (big + little) is explicitly enabled. */
export const DEFAULT_PAINT_MODE: PaintMode = 'exclusive';

/**
 * Resolve densities for a frame kind given the active shell and paint mode.
 * Pure — safe for unit tests and non-React call sites.
 */
export function resolveProjection(
  kind: PaintableFrameKind,
  shell: PaintSurface,
  mode: PaintMode = DEFAULT_PAINT_MODE
): FrameProjection {
  const base = KIND_BASE[kind];
  if (mode === 'dual') {
    return { ...base };
  }
  return {
    dock: shell === 'dock' ? base.dock : 'none',
    window: shell === 'window' ? base.window : 'none',
  };
}

export function densityFor(
  kind: PaintableFrameKind,
  surface: PaintSurface,
  shell: PaintSurface,
  mode: PaintMode = DEFAULT_PAINT_MODE
): PaintDensity {
  return resolveProjection(kind, shell, mode)[surface];
}

/** True when this surface should mount a painter for the kind. */
export function shouldPaint(
  kind: PaintableFrameKind,
  surface: PaintSurface,
  shell: PaintSurface,
  mode: PaintMode = DEFAULT_PAINT_MODE
): boolean {
  return densityFor(kind, surface, shell, mode) !== 'none';
}
